<template>
  <section v-if="dashboardLoading || items.length" class="cso-panel" :class="scope.clsPrefix">
    <div class="panel-head">
      <span class="panel-title">采购成本分析</span>
    </div>
    <div class="panel-body">
      <div v-if="dashboardLoading" class="cost-content skeleton-cost">
        <div class="skeleton-donut"></div>
        <div class="skeleton-list">
          <div class="skeleton-row" v-for="n in 4" :key="n">
            <div class="skeleton-dot"></div>
            <div class="skeleton-line name"></div>
            <div class="skeleton-line bar"></div>
            <div class="skeleton-line value"></div>
          </div>
        </div>
      </div>
      <div v-else class="cost-content">
        <div class="cost-chart">
          <div class="donut-wrapper">
            <svg viewBox="0 0 100 100" class="donut">
              <circle
                v-for="segment in donutSegments"
                :key="segment.name"
                cx="50"
                cy="50"
                r="40"
                fill="none"
                :stroke="segment.color"
                stroke-width="14"
                :stroke-dasharray="segment.dasharray"
                :stroke-dashoffset="segment.dashoffset"
                transform="rotate(-90 50 50)"
              />
              <text x="50" y="42" text-anchor="middle" fill="#9ca3af" font-size="6">月度采购总成本</text>
              <text x="50" y="58" text-anchor="middle" fill="#1f2937" font-size="14" font-weight="800">{{ totalAmountNum }}</text>
              <text x="50" y="70" text-anchor="middle" fill="#9ca3af" font-size="6">万</text>
            </svg>
          </div>
        </div>
        <div class="cost-list">
          <div class="list-item" v-for="item in items" :key="item.name">
            <span class="item-dot" :style="{ background: item.color }"></span>
            <el-tooltip class="item-name-wrap" :content="item.name" placement="top" :disabled="!item.longName">
              <span class="item-name">{{ item.name }}</span>
            </el-tooltip>
            <div class="item-bar-wrapper">
              <div class="item-bar" :style="{ width: item.percent + '%', background: item.color }"></div>
            </div>
            <span class="item-value">{{ item.value }}万</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { REGION, groupByRegion, parseMeta, wanStr, wanStrFixed } from '../utils/externalMetrics'

export default {
  name: 'CsoProcurementCost',
  props: {
    dashboardData: {
      type: Object,
      default: () => ({})
    },
    dashboardLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cso-procurement-cost'
      },
      // 与 CEO costAnalysis.vue 同色阶，单条数据时回退到蓝
      colors: ['#4A5FF3', '#7D8EF5', '#ABB6F9', '#DADFFC', '#E8EEFE', '#F3F6FF']
    }
  },
  computed: {
    // region 1 的记录（已过滤无效、按 sort_index 排序）
    regionItems() {
      const data = this.dashboardData || {}
      const ext = data.externalMetrics
      if (!ext || !Array.isArray(ext.data)) return []
      return groupByRegion(ext.data)[REGION.PROCUREMENT_COST] || []
    },
    // 环形图中心大数 = "月度采购总成本"（元 → 万）
    totalAmountNum() {
      const total = this.regionItems.find(
        (item) => item.display_name === '月度采购总成本'
      )
      return total ? wanStr(total.metric_value) : '0'
    },
    // 列表项 = 排除"合计"的物料组成本拆分（元 → 万，名称取 item_group）
    items() {
      const list = this.regionItems.filter((item) => {
        const name = item.display_name || ''
        if (name.indexOf('物料组成本拆分') === -1) return false
        const itemGroup = this.itemGroupOf(item)
        return itemGroup !== '合计'
      })
      const maxAmount = Math.max(...list.map((item) => this.amountOf(item)), 0)
      return list.map((item, index) => {
        const amount = this.amountOf(item)
        const name = this.itemGroupOf(item)
        return {
          name,
          // 名称是否超过 56px 列宽（此时省略为「…」，需悬停显示气泡）
          longName: this.isNameOverflowing(name),
          // 右侧数值统一保留两位小数（如 158.46 / 25.50）
          value: wanStrFixed(amount),
          amount,
          // 迷你进度条宽度：相对最大类别金额归一化，最低保留 4% 可见
          percent: maxAmount ? Math.max((amount / maxAmount) * 100, 4) : 0,
          color: this.colors[index % this.colors.length]
        }
      })
    },
    donutSegments() {
      const circumference = 251.2
      const total = this.items.reduce((sum, item) => sum + item.amount, 0)
      if (!total) return []
      let offset = 0
      return this.items.map((item) => {
        const length = (item.amount / total) * circumference
        const segment = {
          name: item.name,
          color: item.color,
          // dasharray = 实线段 + 空白段，且空白段等于整圈长度，确保各类别段独立不重叠
          dasharray: `${length} ${circumference}`,
          dashoffset: -offset
        }
        offset += length
        return segment
      })
    }
  },
  methods: {
    // 拆分条的名称取自 meta_json.item_group（如"集成电路"），缺失则从 display_name 去前缀
    itemGroupOf(item) {
      const meta = parseMeta(item.meta_json)
      if (meta.item_group) return meta.item_group
      const name = item.display_name || ''
      const idx = name.indexOf('·')
      return idx !== -1 ? name.slice(idx + 1) : name
    },
    amountOf(item) {
      return Number(item.metric_value) || 0
    },
    // 预估名称是否会超出 56px 名称列（中文约 14px/字、英文数字约 7.5px/字）
    isNameOverflowing(name) {
      const text = String(name || '')
      let width = 0
      for (const ch of text) {
        width += /[一-龥]/.test(ch) ? 14 : 7.5
      }
      return width > 52
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: 'rvc-cosmoplat-workspace-cso-procurement-cost';

.#{$class-prefix} {
  min-height: 0;
  display: flex;
  flex-direction: column;

  .panel-head {
    background: transparent;
    color: var(--text, #1f2937);
    padding: 12px 18px 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .panel-title {
    font-size: 14px;
    font-weight: 600;
    color: rgb(30, 34, 38);
    line-height: 20px;
  }

  .panel-body {
    padding: 8px 18px 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }
  .cost-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    width: 100%;
    min-width: 0;
    min-height: 0;
  }
  .cost-chart {
    position: relative;
    width: 130px;
    height: 130px;
    flex: 0 0 130px;
  }
  .donut-wrapper {
    flex-shrink: 0;
    width: 130px;
    height: 130px;
  }
  .donut {
    width: 100%;
    height: 100%;
  }
  .cost-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .list-item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .item-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .item-name-wrap {
    // el-tooltip 包裹层为 flex 项：承载固定列宽与省略号，保证布局不被挤出
    flex: 0 0 56px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: default;
  }
  .item-name {
    font-size: 13px;
    color: #42526b;
    font-weight: 400;
  }
  .item-bar-wrapper {
    flex: 1 1 0;
    min-width: 24px;
    height: 8px;
    background: #f1f5f9;
    border-radius: 999px;
    overflow: hidden;
  }
  .item-bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease;
  }
  .item-value {
    flex: 0 0 58px;
    font-family: PingFangSC-Semibold;
    font-size: 13px;
    font-weight: 700;
    color: #4A5FF3;
    text-align: right;
    flex-shrink: 0;
  }

  // 骨架屏
  .skeleton-cost {
    justify-content: center;
  }
  .skeleton-donut {
    width: 130px;
    height: 130px;
    flex: 0 0 130px;
    border-radius: 50%;
    background:
      radial-gradient(circle, #fff 42%, transparent 43%),
      linear-gradient(90deg, #eef3fb 25%, #f8fbff 37%, #eef3fb 63%);
    background-size: 100% 100%, 400% 100%;
    animation: cso-pcost-skeleton-loading 1.2s ease-in-out infinite;
  }
  .skeleton-list {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    justify-content: center;
  }
  .skeleton-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .skeleton-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #eef3fb;
    flex-shrink: 0;
  }
  .skeleton-line {
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg, #eef3fb 25%, #f8fbff 37%, #eef3fb 63%);
    background-size: 400% 100%;
    animation: cso-pcost-skeleton-loading 1.2s ease-in-out infinite;
    &.name {
      flex: 0 0 64px;
    }
    &.bar {
      flex: 1 1 0;
      height: 8px;
      min-width: 24px;
    }
    &.value {
      flex: 0 0 58px;
    }
  }
  @keyframes cso-pcost-skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
}
</style>
